import { Cog8ToothIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { motion } from 'framer-motion';
import BackgroundLogins from './backgroundlogins';

export default function LoadingComponent({
  className = 'z-50',
  showBackground = true,
  label = 'Generating Response',
  progress = null,
  stage = '',
  detail = '',
  logs = [],
  showProcessDocAnimation = false,
}) {
  const hasProgress = typeof progress === 'number' && Number.isFinite(progress);
  const clampedProgress = hasProgress ? Math.max(0, Math.min(100, progress)) : 0;
  const orbConfigs = [
    {
      className: 'w-8 h-8 bg-blue-400 opacity-70',
      animate: { x: [0, 200, -100, 150, 0], y: [0, -150, 100, -50, 0], scale: [1, 1.2, 0.8, 1.5, 1] },
      transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
      style: { top: '20%', left: '10%' },
    },
    {
      className: 'w-6 h-6 bg-blue-500 opacity-60',
      animate: { x: [0, -180, 120, -80, 0], y: [0, 120, -80, 160, 0], scale: [1, 0.7, 1.3, 0.9, 1] },
      transition: { duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 },
      style: { top: '60%', right: '15%' },
    },
    {
      className: 'w-10 h-10 bg-blue-300 opacity-50',
      animate: { x: [0, 100, -200, 50, 0], y: [0, -100, 80, -120, 0], scale: [1, 1.1, 0.6, 1.4, 1] },
      transition: { duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 },
      style: { bottom: '20%', left: '20%' },
    },
    {
      className: 'w-7 h-7 bg-purple-400 opacity-70',
      animate: { x: [0, -150, 80, -120, 0], y: [0, 100, -140, 60, 0], scale: [1, 1.3, 0.8, 1.1, 1] },
      transition: { duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
      style: { top: '40%', right: '20%' },
    },
    {
      className: 'w-9 h-9 bg-purple-500 opacity-60',
      animate: { x: [0, 160, -90, 200, 0], y: [0, -80, 120, -160, 0], scale: [1, 0.9, 1.2, 0.7, 1] },
      transition: { duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 1.5 },
      style: { bottom: '30%', right: '10%' },
    },
    {
      className: 'w-5 h-5 bg-purple-300 opacity-80',
      animate: { x: [0, -120, 160, -80, 0], y: [0, -60, 90, -110, 0], scale: [1, 1.4, 0.6, 1.2, 1] },
      transition: { duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 3 },
      style: { top: '10%', right: '30%' },
    },
    {
      className: 'w-4 h-4 bg-blue-200 opacity-40',
      animate: { x: [0, 80, -60, 100, 0], y: [0, -40, 70, -90, 0], scale: [1, 0.8, 1.1, 0.9, 1] },
      transition: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2.5 },
      style: { bottom: '10%', left: '40%' },
    },
    {
      className: 'w-3 h-3 bg-purple-200 opacity-50',
      animate: { x: [0, -70, 90, -40, 0], y: [0, 50, -80, 110, 0], scale: [1, 1.2, 0.7, 1.3, 1] },
      transition: { duration: 8.5, repeat: Infinity, ease: 'easeInOut', delay: 1.8 },
      style: { top: '70%', left: '60%' },
    },
  ];

  return (
    <div
      className={`fixed inset-0 flex items-center justify-center overflow-hidden ${showProcessDocAnimation ? 'bg-cover bg-center' : ''} ${className}`}
      style={showProcessDocAnimation ? { backgroundImage: "url('background.png')" } : undefined}
    >
      {showProcessDocAnimation ? (
        <>
          {orbConfigs.map((orb, index) => (
            <motion.div
              key={`${index}-${orb.className}`}
              className={`absolute rounded-full ${orb.className}`}
              animate={orb.animate}
              transition={orb.transition}
              style={orb.style}
            />
          ))}
        </>
      ) : null}
      {showBackground && !showProcessDocAnimation ? <BackgroundLogins /> : null}

      {/* Loading Spinner */}
      <div className="z-50 flex w-full max-w-xl flex-col items-center gap-4 px-6">
        <div className="relative">
          <Cog8ToothIcon className="h-24 w-24 text-purple-600 animate-spin mb-4" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-8 w-8 rounded-full bg-blue-500 animate-ping opacity-75"></div>
          </div>
        </div>

        <div className="w-full rounded-3xl border border-white/20 bg-slate-950/70 px-6 py-5 shadow-2xl backdrop-blur-xl">
          <p className="text-center text-purple-200 text-xl font-semibold drop-shadow">{label}</p>
          {stage ? <p className="mt-2 text-center text-sm text-slate-300">{stage}</p> : null}
          {detail ? <p className="mt-1 text-center text-xs text-slate-400">{detail}</p> : null}

          {hasProgress ? (
            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-300">
                <span>Progress</span>
                <span>{Math.round(clampedProgress)}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-sky-400 via-cyan-300 to-emerald-300 transition-all duration-500"
                  style={{ width: `${clampedProgress}%` }}
                />
              </div>
            </div>
          ) : null}

          {Array.isArray(logs) && logs.length > 0 ? (
            <div className="mt-5 rounded-2xl border border-white/10 bg-black/30 p-3">
              <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">Job Log</p>
              <div className="space-y-1 font-mono text-xs text-slate-200">
                {logs.map((logLine, index) => (
                  <p key={`${index}-${logLine}`} className="break-words">{logLine}</p>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
